// Celá samolepka s AI: z obrázku od AI (na bílém pozadí) složí hotovou samolepku –
// obrázek + štítek pro jméno + bílý okraj. Tvar je připravený pro řezací plotr:
// spojený obrys bez děr, úzké zářezy zaplněné, okraj zaoblený. Jméno se do štítku
// vypisuje až v aplikaci (Arial, vektorově), takže jde kdykoli změnit.
import type { CustomTemplate } from '../types';

export type AiLayout = 'below' | 'side' | 'none';
export type AiBorder = 'thin' | 'mid' | 'wide';

/** Velikost obrázku ve výpočtu (px). Návrh počítá s obrázkem asi 40 mm → ~22 px na mm. */
const S = 900;
const PX_PER_MM = S / 40;
const BORDER_MM: Record<AiBorder, number> = { thin: 1, mid: 1.8, wide: 2.6 };
/** Zářezy a mezery užší než asi 2 × CLOSE_MM se zaplní (plotr je nevyřízne čistě). */
const CLOSE_MM = 1.4;

const loadImage = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Obrázek se nepodařilo načíst.'));
  img.src = src;
});

/** Odstraní bílé pozadí – jen to, které souvisí s okrajem obrázku (bílé oči apod. zůstanou). */
function removeWhiteBackground(ctx: CanvasRenderingContext2D, w: number, h: number) {
  const id = ctx.getImageData(0, 0, w, h);
  const d = id.data;
  const nearWhite = (i: number) => d[i + 3] < 20 || (d[i] > 228 && d[i + 1] > 228 && d[i + 2] > 228);
  const seen = new Uint8Array(w * h);
  const stack: number[] = [];
  for (let x = 0; x < w; x++) { stack.push(x, (h - 1) * w + x); }
  for (let y = 0; y < h; y++) { stack.push(y * w, y * w + w - 1); }
  while (stack.length) {
    const p = stack.pop()!;
    if (seen[p]) continue;
    if (!nearWhite(p * 4)) continue;
    seen[p] = 1;
    const x = p % w, y = (p / w) | 0;
    if (x > 0) stack.push(p - 1);
    if (x < w - 1) stack.push(p + 1);
    if (y > 0) stack.push(p - w);
    if (y < h - 1) stack.push(p + w);
  }
  for (let p = 0; p < w * h; p++) {
    if (seen[p]) { d[p * 4 + 3] = 0; continue; }
    // měkký přechod u okraje: světlé pixely sousedící s pozadím trochu zprůhlednit
    const x = p % w, y = (p / w) | 0;
    const nb = (x > 0 && seen[p - 1]) || (x < w - 1 && seen[p + 1]) || (y > 0 && seen[p - w]) || (y < h - 1 && seen[p + w]);
    if (nb) {
      const i = p * 4;
      const light = Math.min(d[i], d[i + 1], d[i + 2]);
      if (light > 200) d[i + 3] = Math.round(d[i + 3] * (255 - light) / 55);
    }
  }
  ctx.putImageData(id, 0, 0);
}

/** Přesná vzdálenost (px) každého pixelu k nejbližšímu pixelu masky (Felzenszwalb–Huttenlocher). */
function edt1d(f: Float64Array, n: number, d: Float64Array, v: Int32Array, z: Float64Array) {
  let k = 0; v[0] = 0; z[0] = -Infinity; z[1] = Infinity;
  for (let q = 1; q < n; q++) {
    let s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
    while (s <= z[k]) { k--; s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]); }
    k++; v[k] = q; z[k] = s; z[k + 1] = Infinity;
  }
  k = 0;
  for (let q = 0; q < n; q++) { while (z[k + 1] < q) k++; const dq = q - v[k]; d[q] = dq * dq + f[v[k]]; }
}
function distanceTo(mask: Uint8Array, w: number, h: number): Float32Array {
  const INF = 1e20, n = Math.max(w, h);
  const f = new Float64Array(n), d = new Float64Array(n), z = new Float64Array(n + 1), v = new Int32Array(n);
  const g = new Float64Array(w * h);
  for (let i = 0; i < w * h; i++) g[i] = mask[i] ? 0 : INF;
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) f[y] = g[y * w + x];
    edt1d(f, h, d, v, z);
    for (let y = 0; y < h; y++) g[y * w + x] = d[y];
  }
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) f[x] = g[y * w + x];
    edt1d(f, w, d, v, z);
    for (let x = 0; x < w; x++) out[y * w + x] = Math.sqrt(d[x]);
  }
  return out;
}

/** Díry uvnitř tvaru zaplní (řeže se jen vnější obrys). */
function fillHoles(mask: Uint8Array, w: number, h: number) {
  const out = new Uint8Array(w * h);
  const stack: number[] = [];
  for (let x = 0; x < w; x++) { stack.push(x, (h - 1) * w + x); }
  for (let y = 0; y < h; y++) { stack.push(y * w, y * w + w - 1); }
  while (stack.length) {
    const p = stack.pop()!;
    if (out[p] || mask[p]) continue;
    out[p] = 1;
    const x = p % w, y = (p / w) | 0;
    if (x > 0) stack.push(p - 1);
    if (x < w - 1) stack.push(p + 1);
    if (y > 0) stack.push(p - w);
    if (y < h - 1) stack.push(p + w);
  }
  for (let i = 0; i < w * h; i++) mask[i] = out[i] ? 0 : 1;
}

/** Výrazná barva obrázku – pro rámeček štítku se jménem. */
function accentColor(ctx: CanvasRenderingContext2D, w: number, h: number): string {
  const d = ctx.getImageData(0, 0, w, h).data;
  let best = { s: 0, r: 60, g: 90, b: 160 };
  const buckets = new Map<string, { n: number; r: number; g: number; b: number; s: number }>();
  for (let i = 0; i < d.length; i += 4 * 7) {
    if (d[i + 3] < 200) continue;
    const r = d[i], g = d[i + 1], b = d[i + 2];
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    const sat = mx === 0 ? 0 : (mx - mn) / mx;
    if (sat < 0.35 || mx < 70 || mx > 245) continue;
    const k = `${r >> 5}-${g >> 5}-${b >> 5}`;
    const e = buckets.get(k) || { n: 0, r: 0, g: 0, b: 0, s: 0 };
    e.n++; e.r += r; e.g += g; e.b += b; e.s += sat;
    buckets.set(k, e);
  }
  let top = 0;
  buckets.forEach(e => { if (e.n > top) { top = e.n; best = { s: e.s / e.n, r: e.r / e.n, g: e.g / e.n, b: e.b / e.n }; } });
  // ztmavit, aby rámeček byl dobře vidět
  const f = 0.8;
  return `rgb(${Math.round(best.r * f)},${Math.round(best.g * f)},${Math.round(best.b * f)})`;
}

const roundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

export interface ComposedSticker {
  data: string;
  textZone?: CustomTemplate['textZone'];
  /** výška / šířka */
  ratio: number;
}

/** Připraví obrázek od AI (bez pozadí, oříznutý). Výsledek se pak skládá v různých rozloženích. */
export async function prepareAiArt(src: string): Promise<HTMLCanvasElement> {
  const img = await loadImage(src);
  const c = document.createElement('canvas');
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0);
  removeWhiteBackground(ctx, c.width, c.height);
  const d = ctx.getImageData(0, 0, c.width, c.height).data;
  let minX = c.width, minY = c.height, maxX = -1, maxY = -1;
  for (let y = 0; y < c.height; y++) for (let x = 0; x < c.width; x++) {
    if (d[(y * c.width + x) * 4 + 3] > 40) { if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y; }
  }
  if (maxX < 0) throw new Error('Na obrázku od AI nic není, zkuste jiný návrh.');
  const out = document.createElement('canvas');
  out.width = maxX - minX + 1; out.height = maxY - minY + 1;
  out.getContext('2d')!.drawImage(c, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
  return out;
}

export function composeAiSticker(art: HTMLCanvasElement, layout: AiLayout, border: AiBorder): ComposedSticker {
  const k = S / Math.max(art.width, art.height);
  const aw = Math.round(art.width * k), ah = Math.round(art.height * k);
  const lem = BORDER_MM[border] * PX_PER_MM;
  const close = CLOSE_MM * PX_PER_MM;
  const pad = Math.ceil(lem + close + 6);

  // rozložení obrázku a štítku se jménem
  let ax = 0, ay = 0, plate: { x: number; y: number; w: number; h: number } | null = null;
  if (layout === 'below') {
    const pw = Math.max(aw * 0.9, S * 0.85), ph = S * 0.3;
    const W = Math.max(aw, pw);
    ax = (W - aw) / 2; ay = 0;
    plate = { x: (W - pw) / 2, y: ah - ph * 0.18, w: pw, h: ph };
  } else if (layout === 'side') {
    const pw = S * 1.15, ph = S * 0.4;
    const H = Math.max(ah, ph);
    ax = 0; ay = (H - ah) / 2;
    plate = { x: aw - Math.min(aw * 0.12, S * 0.08), y: (H - ph) / 2, w: pw, h: ph };
  }
  const contentW = Math.max(ax + aw, plate ? plate.x + plate.w : 0);
  const contentH = Math.max(ay + ah, plate ? plate.y + plate.h : 0);
  const W = Math.ceil(contentW + pad * 2), H = Math.ceil(contentH + pad * 2);
  ax += pad; ay += pad;
  if (plate) { plate.x += pad; plate.y += pad; }

  const accent = accentColor(art.getContext('2d')!, art.width, art.height);

  // vrstva s obrázkem a štítkem
  const top = document.createElement('canvas');
  top.width = W; top.height = H;
  const tctx = top.getContext('2d', { willReadFrequently: true })!;
  tctx.drawImage(art, ax, ay, aw, ah);
  if (plate) {
    // štítek se jménem leží nad obrázkem, aby jméno nic nezakrylo
    roundRect(tctx, plate.x, plate.y, plate.w, plate.h, plate.h * 0.38);
    tctx.fillStyle = '#ffffff'; tctx.fill();
    tctx.lineWidth = Math.max(6, S * 0.012); tctx.strokeStyle = accent; tctx.stroke();
  }

  // tvar samolepky: obrázek ∪ štítek → bez děr → rozšířit o okraj + zaplnit úzké zářezy
  const td = tctx.getImageData(0, 0, W, H).data;
  const mask = new Uint8Array(W * H);
  for (let i = 0; i < W * H; i++) mask[i] = td[i * 4 + 3] > 40 ? 1 : 0;
  fillHoles(mask, W, H);
  const dOut = distanceTo(mask, W, H);
  const grown = new Uint8Array(W * H);
  for (let i = 0; i < W * H; i++) grown[i] = dOut[i] <= lem + close ? 1 : 0;
  const notGrown = new Uint8Array(W * H);
  for (let i = 0; i < W * H; i++) notGrown[i] = grown[i] ? 0 : 1;
  // okraj plátna počítat jako „venku“
  for (let x = 0; x < W; x++) { notGrown[x] = 1; notGrown[(H - 1) * W + x] = 1; }
  for (let y = 0; y < H; y++) { notGrown[y * W] = 1; notGrown[y * W + W - 1] = 1; }
  const dIn = distanceTo(notGrown, W, H);

  const out = document.createElement('canvas');
  out.width = W; out.height = H;
  const octx = out.getContext('2d')!;
  const bg = octx.createImageData(W, H);
  let minX = W, minY = H, maxX = -1, maxY = -1;
  for (let i = 0; i < W * H; i++) {
    const a = Math.max(0, Math.min(1, dIn[i] - close + 0.5));
    if (a <= 0) continue;
    bg.data[i * 4] = 255; bg.data[i * 4 + 1] = 255; bg.data[i * 4 + 2] = 255; bg.data[i * 4 + 3] = Math.round(a * 255);
    const x = i % W, y = (i / W) | 0;
    if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y;
  }
  octx.putImageData(bg, 0, 0);
  octx.drawImage(top, 0, 0);

  // oříznout na tvar (+ malá rezerva pro čáru řezu)
  const m = 4;
  const cx = Math.max(0, minX - m), cy = Math.max(0, minY - m);
  const cw = Math.min(W, maxX + m + 1) - cx, ch = Math.min(H, maxY + m + 1) - cy;
  const fin = document.createElement('canvas');
  fin.width = cw; fin.height = ch;
  fin.getContext('2d')!.drawImage(out, cx, cy, cw, ch, 0, 0, cw, ch);

  let textZone: ComposedSticker['textZone'];
  if (plate) {
    const ix = plate.h * 0.22, iy = plate.h * 0.14;
    textZone = {
      xPct: (plate.x + ix - cx) / cw,
      yPct: (plate.y + iy - cy) / ch,
      wPct: (plate.w - ix * 2) / cw,
      hPct: (plate.h - iy * 2) / ch,
      align: 'center',
    };
  }
  return { data: fin.toDataURL('image/png'), textZone, ratio: ch / cw };
}

export const AI_TEMPLATES_KEY = 'samolepky-ai-templates';
